import { METAL_PROFILES } from '../data/chemistry/metalProfiles';
import {
  NOBLE_GAS_SYMBOLS,
  NONMETAL_PAIR_REACTIONS,
  NONMETAL_RADIOACTIVE_SYMBOLS,
  NONMETAL_TOXIC_SYMBOLS,
  TOO_RARE_SYMBOLS,
} from '../data/chemistry/nonmetalPairs';
import { CONDITIONS, RADIOACTIVE_SAFETY_NOTE, REACTION_CLASSES, TOXIC_SAFETY_NOTE } from '../data/chemistry/reactionClasses';
import { buildReactionEquationText, createPairKey } from '../utils/reactionUtils';
import { buildBinaryFormula } from '../utils/formulaUtils';
import { buildHydroxideFormula, nameBinaryCompound, nameHydroxide } from '../utils/compoundNameUtils';

const HALOGENS = ['F', 'Cl', 'Br', 'I'];
const CHALCOGENS = ['Se', 'Te'];
const MIXTURE_CLASSES = ['alloy', 'amalgam'];
const WATER_ID = 'H2O';
const ACID_ID = 'HCl';

// Metals known to dissolve directly in mercury. Others barely dissolve or not at all
// (iron doesn't, which is why mercury is stored in iron flasks).
const AMALGAM_SYMBOLS = ['Li', 'Na', 'K', 'Rb', 'Cs', 'Mg', 'Ca', 'Sr', 'Ba', 'Al', 'Zn', 'Cd', 'Ga', 'In', 'Tl', 'Sn', 'Pb', 'Bi', 'Cu', 'Ag', 'Au', 'Pd'];

// Metals known to form polonides when heated with polonium.
const POLONIDES = {
  Na: 'Na2Po', K: 'K2Po', Be: 'BePo', Mg: 'MgPo', Ca: 'CaPo', Sr: 'SrPo', Ba: 'BaPo',
  Zn: 'ZnPo', Cd: 'CdPo', Hg: 'HgPo', Pb: 'PbPo', Ni: 'NiPo', Pt: 'PtPo2',
};

const WATER_CLASSES = {
  explosive: 'water-explosive',
  violent: 'water-violent',
  fizz: 'water-fizz',
  slow: 'water-slow',
  steam: 'water-steam',
  none: 'water-none',
};

const ACID_CLASSES = { violent: 'acid-violent', fizz: 'acid-fizz', slow: 'acid-slow', none: 'acid-none' };

/**
 * Works out what happens when any two substances are mixed, using the researched chemistry
 * profiles. Returns a plain description that ReactionEngine turns into a Reaction.
 */
export default class ReactionPredictor {
  constructor(substancesById) {
    this.substancesById = substancesById;
    this.nonmetalPairs = new Map(NONMETAL_PAIR_REACTIONS.map((entry) => [createPairKey(...entry.pair), entry]));
  }

  predict(firstId, secondId) {
    const first = this.substancesById.get(firstId);
    const second = this.substancesById.get(secondId);
    const outcome = this.findOutcome(first, second);
    return this.finishOutcome(outcome, first, second);
  }

  findOutcome(first, second) {
    if ([first, second].some((substance) => TOO_RARE_SYMBOLS.includes(substance.id))) {
      return { reactionClass: 'too-rare', productName: 'Nothing: never tried' };
    }

    const tableEntry = this.nonmetalPairs.get(createPairKey(first.id, second.id));
    if (tableEntry) return this.describeTableReaction(tableEntry, first, second);

    if ([first, second].some((substance) => NOBLE_GAS_SYMBOLS.includes(substance.id))) {
      return { reactionClass: 'noble-gas', productName: 'Nothing new' };
    }

    const compound = [first, second].find((substance) => !substance.isElement());
    if (compound) {
      const other = compound === first ? second : first;
      return this.describeCompoundReaction(compound, other);
    }

    const firstProfile = METAL_PROFILES[first.id];
    const secondProfile = METAL_PROFILES[second.id];
    if (firstProfile && secondProfile) return this.describeMetalPair(first, second);
    if (firstProfile) return this.describeMetalWithNonmetal(first, firstProfile, second);
    if (secondProfile) return this.describeMetalWithNonmetal(second, secondProfile, first);
    return { reactionClass: 'no-direct', productName: 'Nothing new' };
  }

  describeTableReaction(entry, first, second) {
    if (entry.alloyName) {
      return { reactionClass: entry.reactionClass, productName: entry.alloyName, conditions: entry.conditions };
    }
    return {
      reactionClass: entry.reactionClass,
      productName: entry.name,
      products: [entry.product],
      conditions: entry.conditions,
      funFact: entry.funFact,
    };
  }

  describeCompoundReaction(compound, other) {
    const profile = METAL_PROFILES[other.id];
    if (!profile) return { reactionClass: 'no-direct', productName: 'Nothing new' };

    if (compound.id === WATER_ID) {
      const reactionClass = WATER_CLASSES[profile.water ?? 'none'];
      if (reactionClass === 'water-none') return { reactionClass, productName: 'Nothing new' };
      if (profile.waterProduct) {
        return {
          reactionClass,
          products: [profile.waterProduct, 'H2'],
          productName: `${nameBinaryCompound(other, profile.waterProduct, 'O', { useRomanNumeral: profile.variable })} + hydrogen gas`,
        };
      }
      const charge = profile.waterN ?? profile.n;
      return {
        reactionClass,
        products: [buildHydroxideFormula(other.id, charge), 'H2'],
        productName: `${nameHydroxide(other, charge, { useRomanNumeral: profile.variable })} + hydrogen gas`,
      };
    }

    if (compound.id === ACID_ID) {
      const reactionClass = ACID_CLASSES[profile.acid ?? 'none'];
      if (reactionClass === 'acid-none') return { reactionClass, productName: 'Nothing new' };
      const chloride = buildBinaryFormula(other.id, profile.acidN ?? profile.n, 'Cl', 1);
      return {
        reactionClass,
        products: [chloride, 'H2'],
        productName: `${nameBinaryCompound(other, chloride, 'Cl', { useRomanNumeral: profile.variable })} + hydrogen gas`,
      };
    }
    return { reactionClass: 'no-direct', productName: 'Nothing new' };
  }

  describeMetalPair(first, second) {
    const hasMercury = first.id === 'Hg' || second.id === 'Hg';
    const polonium = [first, second].find((substance) => substance.id === 'Po');
    if (polonium) {
      const metal = polonium === first ? second : first;
      return this.describeMetalWithNonmetal(metal, METAL_PROFILES[metal.id], polonium);
    }
    if (hasMercury) {
      const other = first.id === 'Hg' ? second : first;
      if (!AMALGAM_SYMBOLS.includes(other.id)) return { reactionClass: 'no-direct', productName: 'Nothing new' };
      return { reactionClass: 'amalgam', productName: `${other.name} amalgam (a mercury mixture)` };
    }
    return { reactionClass: 'alloy', productName: `An alloy of ${first.name} and ${second.name}` };
  }

  describeMetalWithNonmetal(metal, profile, partner) {
    const symbol = partner.id;
    const nameOptions = { useRomanNumeral: profile.variable };
    const compoundOutcome = (reactionClass, formula, anionSymbol = symbol) => ({
      reactionClass,
      products: [formula],
      productName: nameBinaryCompound(metal, formula, anionSymbol, nameOptions),
    });
    const noReaction = { reactionClass: 'no-direct', productName: 'Nothing new' };

    if (symbol === 'O') {
      if (!profile.O) return { reactionClass: 'noble-oxygen', productName: 'Nothing new' };
      return compoundOutcome(profile.burns ? 'oxide-burn' : 'oxide-slow', profile.O);
    }

    if (HALOGENS.includes(symbol)) {
      const override = profile.halides?.[symbol];
      if (override === null) return noReaction;
      const formula = override ?? buildBinaryFormula(metal.id, profile.n, symbol, 1);
      return compoundOutcome(this.pickHalideClass(profile, symbol), formula);
    }

    if (symbol === 'S') {
      if (!profile.S) return noReaction;
      return compoundOutcome(this.pickSulfideClass(profile), profile.S);
    }

    if (symbol === 'Po') {
      return POLONIDES[metal.id] ? compoundOutcome('chalcogenide', POLONIDES[metal.id]) : noReaction;
    }

    if (CHALCOGENS.includes(symbol)) {
      const formula = profile[symbol] ?? (profile.chalc && profile.S ? profile.S.replace(/S(?![a-z])/g, symbol) : null);
      if (!formula) return noReaction;
      return compoundOutcome('chalcogenide', formula);
    }

    const simpleClasses = { N: 'nitride', H: 'hydride', C: 'carbide', B: 'boride-silicide', Si: 'boride-silicide', P: 'phosphide-arsenide', As: 'phosphide-arsenide' };
    if (simpleClasses[symbol]) {
      if (!profile[symbol]) return noReaction;
      return compoundOutcome(simpleClasses[symbol], profile[symbol]);
    }

    return noReaction;
  }

  pickHalideClass(profile, halogen) {
    if (profile.halideEffect === 'slow') return 'halide-slow';
    if (profile.halideEffect === 'flash' || profile.vigor >= 3 || halogen === 'F') return 'halide-flash';
    return 'halide-heat';
  }

  pickSulfideClass(profile) {
    if (profile.sulfideEffect === 'slow') return 'sulfide-slow';
    if (profile.sulfideEffect === 'flash' || profile.vigor >= 3) return 'sulfide-flash';
    return 'sulfide-heat';
  }

  /**
   * Adds the spoken texts, effect, conditions, equation and safety notes.
   */
  finishOutcome(outcome, first, second) {
    const reactionClass = REACTION_CLASSES[outcome.reactionClass];
    const isRadioactive = [first, second].some(
      (substance) => METAL_PROFILES[substance.id]?.radioactive || NONMETAL_RADIOACTIVE_SYMBOLS.includes(substance.id)
    );
    const isToxic = [first, second].some(
      (substance) => METAL_PROFILES[substance.id]?.toxic || NONMETAL_TOXIC_SYMBOLS.includes(substance.id)
    );
    const hasReaction = reactionClass.effect !== 'none';

    let conditions = outcome.conditions ?? reactionClass.conditions;
    if (isRadioactive && hasReaction) conditions = CONDITIONS.NUCLEAR_LAB;

    let safetyNote = reactionClass.safetyNote ?? '';
    if (isRadioactive) safetyNote = RADIOACTIVE_SAFETY_NOTE;
    else if (isToxic) safetyNote = TOXIC_SAFETY_NOTE;

    return {
      reactantIds: [first.id, second.id],
      reactionClass: outcome.reactionClass,
      title: reactionClass.title,
      explanation: reactionClass.explanation,
      funFact: outcome.funFact ?? reactionClass.funFact,
      safetyNote,
      effect: reactionClass.effect,
      conditions,
      productName: outcome.productName,
      equationText: outcome.products ? buildReactionEquationText([first, second], outcome.products) : '',
      isMixture: MIXTURE_CLASSES.includes(outcome.reactionClass),
    };
  }
}
